/**
 * Navigation Group Component
 * Expandable menu group with sub-items
 */

import { Link, useTranslation } from '@ufc/core'
import { cn, ChevronDown } from '@ufc/uikit'
import type { NavGroupProps } from '../types'
import { CollapsedSubmenu } from './collapsed-submenu'

// ============================================
// Helper Functions
// ============================================

function getGroupButtonStyles(isActive: boolean): string {
  const baseStyles = 'w-full flex items-center rounded-xl text-sm font-medium transition-colors duration-200 gap-3 px-4 py-3.5'
  const activeStyles = 'bg-primary/10 dark:bg-primary/20 text-primary'
  const inactiveStyles = 'text-muted-foreground hover:bg-secondary hover:text-foreground'

  return cn(baseStyles, isActive ? activeStyles : inactiveStyles)
}

function getSubItemStyles(isActive: boolean): string {
  const baseStyles = 'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200'
  const activeStyles = 'bg-gradient-to-r from-primary to-primary/90 text-primary-foreground shadow-md shadow-primary/20'
  const inactiveStyles = 'text-muted-foreground hover:bg-secondary hover:text-foreground'

  return cn(baseStyles, isActive ? activeStyles : inactiveStyles)
}

function getChevronStyles(isExpanded: boolean): string {
  return cn('w-4 h-4 transition-transform duration-200', isExpanded && 'rotate-180')
}

// ============================================
// Sub-Components
// ============================================

interface ExpandedGroupProps {
  group: NavGroupProps['group']
  isActive: boolean
  isExpanded: boolean
  currentPath: string
  onToggle: () => void
  onNavigate?: () => void
  t: (key: string) => string
}

function ExpandedGroup({
  group,
  isActive,
  isExpanded,
  currentPath,
  onToggle,
  onNavigate,
  t,
}: ExpandedGroupProps) {
  const Icon = group.icon

  return (
    <>
      <button
        onClick={onToggle}
        className={getGroupButtonStyles(isActive)}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        <span className="flex-1 text-start truncate">{t(group.labelKey)}</span>
        <ChevronDown className={getChevronStyles(isExpanded)} />
      </button>
      {isExpanded && (
        <ul className="mt-1.5 ms-4 space-y-1 border-s border-border/50 ps-4">
          {group.items.map((item) => {
            const isItemActive = currentPath === item.to
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  onClick={onNavigate}
                  className={getSubItemStyles(isItemActive)}
                >
                  <item.icon className="w-4 h-4" />
                  <span className="flex-1 text-start truncate">{t(item.labelKey)}</span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}

// ============================================
// Component
// ============================================

export function NavGroup({
  group,
  collapsed,
  isExpanded,
  isActive,
  isRTL,
  onToggle,
  onNavigate,
  currentPath,
}: NavGroupProps) {
  const { t } = useTranslation()

  if (collapsed) {
    return (
      <CollapsedSubmenu
        icon={group.icon}
        label={t(group.labelKey)}
        items={group.items}
        isActive={isActive}
        isRTL={isRTL}
        onNavigate={onNavigate}
      />
    )
  }

  return (
    <ExpandedGroup
      group={group}
      isActive={isActive}
      isExpanded={isExpanded}
      currentPath={currentPath}
      onToggle={onToggle}
      onNavigate={onNavigate}
      t={t}
    />
  )
}
